import { useTranslation } from "react-i18next"
import { UserListItem } from "@repo/core/models"
import invariant from "tiny-invariant"

import { Button } from "@/components/ui/button"
import {
  DialogBody,
  DialogBodyError,
  DialogBodySuccess,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"

import { useMutationResendPassword } from "../users-api"

type Props = {
  close: (value: boolean) => void
  user: UserListItem
}
export function DialogResendPassword({ close, user }: Props) {
  const { t } = useTranslation()

  invariant(user.cognito_user_id)
  const mutation = useMutationResendPassword(user.cognito_user_id)

  const handleSave = (event: React.FormEvent) => {
    event.preventDefault()
    mutation.mutateAsync()
  }

  return (
    <DialogContent>
      <form onSubmit={handleSave}>
        <DialogHeader>
          <DialogTitle>{t("users.resendPassword.action")}</DialogTitle>
        </DialogHeader>
        {(mutation.isIdle || mutation.isPending) && (
          <>
            <DialogBody className="flex flex-col gap-4">
              <p>{t("users.resendPassword.confirm")}</p>
              <div>
                <code
                  className="
                    bg-muted px-[0.3rem] py-[0.2rem] font-mono text-sm
                    font-semibold
                  "
                >
                  {user.email}
                </code>
              </div>
            </DialogBody>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => close(false)}
                isDisabled={mutation.isPending}
              >
                {t("global.button.cancel")}
              </Button>
              <Button type="submit" isLoading={mutation.isPending}>
                {t("users.resendPassword.action")}
              </Button>
            </DialogFooter>
          </>
        )}
        {mutation.isSuccess && (
          <>
            <DialogBodySuccess>
              {t("users.resendPassword.success", { email: user.email })}
            </DialogBodySuccess>
            <DialogFooter>
              <Button variant="outline" onClick={() => close(false)}>
                {t("global.button.close")}
              </Button>
            </DialogFooter>
          </>
        )}
        {mutation.isError && (
          <>
            <DialogBodyError>
              Unable to resend the password to {user.email}
            </DialogBodyError>
            <DialogFooter>
              <Button variant="outline" onClick={() => close(false)}>
                {t("global.button.close")}
              </Button>
            </DialogFooter>
          </>
        )}
      </form>
    </DialogContent>
  )
}
